import { useCustom, useCustomMutation } from "@refinedev/core";
import { Button, Card, InputNumber, Space, Tag, Typography, notification } from "antd";
import { useEffect, useState } from "react";
import { apiUrl } from "@/consts";

const { Text } = Typography;

// Backend wraps the payload in `data`, so the useCustom result nests one extra
// level: useCustomResult.data (CustomResponse) → .data (response body) → .data.
type CommissionBody = {
  data: {
    commissionPercent: number;
    defaultPercent: number;
    isOverridden: boolean;
    updatedAt: string | null;
    updatedByEmail: string | null;
  };
};

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

// Global lotto commission — the platform's default cut of every club's lotto
// sales, applied to clubs that have no per-club override (set on the club edit
// page). Reads/writes GET|PATCH /admin/lotto-commission. PATCH with null resets
// to the hardcoded platform default. SUPER_ADMIN only (enforced server-side; the
// card is also hidden for non-super admins on the settings page).
export const LottoCommissionCard = () => {
  const [value, setValue] = useState<number | null>(null);

  const { data, isLoading, refetch } = useCustom<CommissionBody>({
    url: `${apiUrl}/lotto-commission`,
    method: "get",
  });

  const body = data?.data?.data;
  const commissionPercent = body?.commissionPercent;
  const defaultPercent = body?.defaultPercent;
  const isOverridden = body?.isOverridden ?? false;
  const updatedOn = formatDate(body?.updatedAt ?? null);
  const updatedBy = body?.updatedByEmail ?? null;

  // Seed the input once the current value loads (and after a refetch).
  useEffect(() => {
    if (commissionPercent !== undefined) setValue(commissionPercent);
  }, [commissionPercent]);

  const { mutate, isLoading: saving } = useCustomMutation();

  function save(commission: number | null, successMessage: string) {
    mutate(
      {
        url: `${apiUrl}/lotto-commission`,
        method: "patch",
        values: { commissionPercent: commission },
      },
      {
        onSuccess: () => {
          notification.success({ message: successMessage });
          refetch();
        },
        onError: (err: any) =>
          notification.error({
            message: "Failed to update lotto commission",
            description:
              err?.response?.data?.error ?? err?.message ?? "An unexpected error occurred.",
          }),
      }
    );
  }

  const dirty = value !== null && value !== commissionPercent;

  return (
    <Card title="Lotto commission" style={{ width: "70vw" }} loading={isLoading}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Text type="secondary">
          The platform's commission on club lotto sales, applied to every club that
          doesn't have a per-club override. Per-club overrides are set on each club's
          edit page.
        </Text>

        <Space align="center" wrap>
          <Text strong>Current:</Text>
          {commissionPercent !== undefined ? (
            isOverridden ? (
              <Tag color="blue">Set to {commissionPercent}%</Tag>
            ) : (
              <Tag>Using platform default ({defaultPercent}%)</Tag>
            )
          ) : null}
          {isOverridden && (updatedBy || updatedOn) && (
            <Text type="secondary">
              last changed{updatedBy ? ` by ${updatedBy}` : ""}
              {updatedOn ? ` on ${updatedOn}` : ""}
            </Text>
          )}
        </Space>

        <Space align="center" wrap>
          <Text strong>Global commission</Text>
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            value={value ?? undefined}
            onChange={(v) => setValue(v == null ? null : Number(v))}
            addonAfter="%"
            style={{ width: 140 }}
          />
          <Button
            type="primary"
            loading={saving}
            disabled={!dirty}
            onClick={() => save(value, "Global lotto commission updated")}
          >
            Save
          </Button>
          <Button
            loading={saving}
            disabled={!isOverridden}
            onClick={() => save(null, "Lotto commission reset to platform default")}
          >
            Reset to default
          </Button>
        </Space>

        {defaultPercent !== undefined && (
          <Text type="secondary">Platform default: {defaultPercent}%</Text>
        )}
      </Space>
    </Card>
  );
};
